<div class="form-editor">
  <header class="editor-header">
    <h1>{{ isEditMode ? ('admin.editor.title.edit' | localize) : ('admin.editor.title.create' | localize) }}</h1>
    <a routerLink="/admin/forms" class="btn-back">{{ 'admin.editor.backToList' | localize }}</a>
  </header>

  @if (actionSuccess()) {
    <div class="notification is-success">
      <button class="delete" (click)="dismissActionSuccess()"></button>
      {{ actionSuccess() }}
    </div>
  }

  @if (actionError()) {
    <div class="notification is-danger">
      <button class="delete" (click)="dismissActionError()"></button>
      {{ actionError() }}
    </div>
  }

  @if (error) {
    <div class="error-message">{{ error }}</div>
  }

  @if (loading) {
    <div class="loading">{{ 'admin.editor.loading' | localize }}</div>
  } @else {
    <form [formGroup]="form" (ngSubmit)="onSubmit()">
      <section class="form-section">
        <h2>{{ 'admin.editor.section.basic' | localize }}</h2>
        <div class="form-row">
          <div class="form-field">
            <label for="formId">{{ 'admin.editor.field.formId' | localize }} {{ 'common.required' | localize }}</label>
            <input id="formId" type="text" formControlName="formId"
                   [placeholder]="'admin.editor.field.formId.placeholder' | localize"
                   [class.readonly]="isEditMode">
          </div>
          <div class="form-field">
            <label for="version">{{ 'admin.editor.field.version' | localize }} {{ 'common.required' | localize }}</label>
            <input id="version" type="text" formControlName="version"
                   [placeholder]="'admin.editor.field.version.placeholder' | localize"
                   [class.readonly]="isEditMode">
          </div>
        </div>
        <div class="form-row">
          <div class="form-field">
            <label for="title">{{ 'admin.editor.field.title' | localize }} {{ 'common.required' | localize }}</label>
            <input id="title" type="text" formControlName="title" [placeholder]="'admin.editor.field.title.placeholder' | localize">
          </div>
        </div>
      </section>

      <section class="form-section">
        <h2>Logo</h2>

        @if (existingLogoUrl && !logoRemoved) {
          <div class="logo-preview">
            <img [src]="existingLogoUrl" alt="Form logo" class="logo-preview-image">
            <button type="button" class="btn-remove" (click)="removeLogo()">Remove</button>
          </div>
        }

        <app-file-uploads
          #logoUpload
          fieldName="logo"
          [config]="logoUploadConfig">
        </app-file-uploads>
      </section>

      <section class="form-section">
        <h2>{{ 'admin.editor.metadata.title' | localize }}</h2>
        
        <div class="form-row">
          <div class="form-field">
            <label for="insuranceCompany">{{ 'admin.editor.metadata.insuranceCompany' | localize }} {{ 'common.required' | localize }}</label>
            <input id="insuranceCompany" type="text" formControlName="insuranceCompany" 
                   [placeholder]="'admin.editor.metadata.insuranceCompany' | localize">
            @if (form.get('insuranceCompany')?.invalid && form.get('insuranceCompany')?.touched) {
              <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
            }
          </div>
          <div class="form-field">
            <label for="natCatType">{{ 'admin.editor.metadata.natCatType' | localize }} {{ 'common.required' | localize }}</label>
            <input id="natCatType" type="text" formControlName="natCatType" 
                   [placeholder]="'admin.editor.metadata.natCatType' | localize">
            @if (form.get('natCatType')?.invalid && form.get('natCatType')?.touched) {
              <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
            }
          </div>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label for="natCatName">{{ 'admin.editor.metadata.natCatName' | localize }} {{ 'common.required' | localize }}</label>
            <input id="natCatName" type="text" formControlName="natCatName" 
                   [placeholder]="'admin.editor.metadata.natCatName' | localize">
            @if (form.get('natCatName')?.invalid && form.get('natCatName')?.touched) {
              <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
            }
          </div>
          <div class="form-field">
            <label for="location">{{ 'admin.editor.metadata.location' | localize }} {{ 'common.required' | localize }}</label>
            <input id="location" type="text" formControlName="location" 
                   [placeholder]="'admin.editor.metadata.location' | localize">
            @if (form.get('location')?.invalid && form.get('location')?.touched) {
              <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
            }
          </div>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label for="year">{{ 'admin.editor.metadata.year' | localize }} {{ 'common.required' | localize }}</label>
            <input id="year" type="text" formControlName="year" 
                   [placeholder]="'admin.editor.metadata.year' | localize">
            @if (form.get('year')?.invalid && form.get('year')?.touched) {
              <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
            }
          </div>
          <div class="form-field">
            <label for="egressEmail">{{ 'admin.editor.metadata.egressEmail' | localize }} {{ 'common.required' | localize }}</label>
            <input id="egressEmail" type="email" formControlName="egressEmail" 
                   [placeholder]="'admin.editor.metadata.egressEmail' | localize">
            @if (form.get('egressEmail')?.invalid && form.get('egressEmail')?.touched) {
              @if (form.get('egressEmail')?.hasError('required')) {
                <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
              }
              @if (form.get('egressEmail')?.hasError('email')) {
                <p class="error-text">{{ 'admin.editor.metadata.error.email' | localize }}</p>
              }
            }
          </div>
        </div>

        <div class="custom-metadata-section">
          <h3>{{ 'admin.editor.metadata.customFields' | localize }}</h3>
          
          @if (customMetadataFields.length > 0) {
            <div class="custom-fields-list">
              @for (field of customMetadataFields.controls; track $index; let i = $index) {
                <div class="custom-field-row" [formGroup]="$any(field)">
                  <div class="form-field">
                    <label>{{ 'admin.editor.metadata.customKey' | localize }} {{ 'common.required' | localize }}</label>
                    <input type="text" formControlName="key" 
                           [placeholder]="'admin.editor.metadata.customKey' | localize">
                    @if (field.get('key')?.invalid && field.get('key')?.touched) {
                      @if (field.get('key')?.hasError('required')) {
                        <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
                      }
                      @if (field.get('key')?.hasError('alphanumeric')) {
                        <p class="error-text">{{ 'admin.editor.metadata.error.alphanumeric' | localize }}</p>
                      }
                      @if (field.get('key')?.hasError('predefinedKeyConflict')) {
                        <p class="error-text">{{ 'admin.editor.metadata.error.predefinedKeyConflict' | localize }}</p>
                      }
                    }
                  </div>
                  <div class="form-field">
                    <label>{{ 'admin.editor.metadata.customValue' | localize }} {{ 'common.required' | localize }}</label>
                    <input type="text" formControlName="value" 
                           [placeholder]="'admin.editor.metadata.customValue' | localize">
                    @if (field.get('value')?.invalid && field.get('value')?.touched) {
                      <p class="error-text">{{ 'admin.editor.metadata.error.required' | localize }}</p>
                    }
                  </div>
                  <button type="button" class="btn-remove" (click)="removeCustomMetadataField(i)">
                    {{ 'admin.editor.metadata.remove' | localize }}
                  </button>
                </div>
              }
            </div>
          }
          
          @if (customMetadataFields.length >= 5) {
            <p class="error-text">{{ 'admin.editor.metadata.error.maxCustomFields' | localize }}</p>
          }
          
          <button type="button" class="btn-add" (click)="addCustomMetadataField()" 
                  [disabled]="customMetadataFields.length >= 5">
            {{ 'admin.editor.metadata.addCustomField' | localize }}
          </button>
        </div>
      </section>

      <section class="form-section">
        <header class="section-header">
          <h2>{{ 'admin.editor.landingPage.title' | localize }}</h2>
        </header>

        <div class="form-field checkbox-field">
          <label>
            <input
              type="checkbox"
              [checked]="landingPageEnabled"
              (change)="toggleLandingPage($any($event.target).checked)"
            >
            {{ 'admin.editor.landingPage.toggle' | localize }}
          </label>
        </div>

        @if (landingPageEnabled) {
          <div class="landing-page-config">
            <div class="form-row">
              <div class="form-field">
                <label for="landingPageTitle">{{ 'admin.editor.landingPage.titleField' | localize }} {{ 'common.required' | localize }}</label>
                <input id="landingPageTitle" type="text" formControlName="landingPageTitle"
                       [placeholder]="'admin.editor.landingPage.titleField.placeholder' | localize">
                @if (landingPageTitleError) {
                  <p class="error-text">{{ 'admin.editor.landingPage.error.titleRequired' | localize }}</p>
                }
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <label for="landingPageDescription">{{ 'admin.editor.landingPage.description' | localize }}</label>
                <textarea id="landingPageDescription" formControlName="landingPageDescription"
                          [placeholder]="'admin.editor.landingPage.description.placeholder' | localize"
                          rows="3"></textarea>
              </div>
            </div>

            <div class="checklist-section">
              <h3>{{ 'admin.editor.landingPage.checklist' | localize }}</h3>

              @if (checklistItems.length === 0) {
                <div class="empty-fields">
                  <p>{{ 'admin.editor.landingPage.checklist' | localize }}</p>
                </div>
              } @else {
                <div class="checklist-items-list" cdkDropList (cdkDropListDropped)="onChecklistItemDrop($event)">
                  @for (item of checklistItems; track $index; let i = $index) {
                    <div class="checklist-item-card" cdkDrag>
                      <div cdkDragPlaceholder class="drag-placeholder"></div>
                      <div class="checklist-item-header">
                        <span class="field-position">{{ i + 1 }}</span>
                        <span class="drag-handle" cdkDragHandle>
                          <i class="fa-solid fa-grip-vertical"></i>
                        </span>
                        <div class="reorder-buttons">
                          <button #checklistMoveUpBtn type="button" class="btn-reorder" (click)="moveChecklistItemUp(i)" [disabled]="isFirstChecklistItem(i)" aria-label="Move checklist item up">
                            <i class="fa-solid fa-arrow-up"></i>
                          </button>
                          <button #checklistMoveDownBtn type="button" class="btn-reorder" (click)="moveChecklistItemDown(i)" [disabled]="isLastChecklistItem(i)" aria-label="Move checklist item down">
                            <i class="fa-solid fa-arrow-down"></i>
                          </button>
                        </div>
                      </div>
                      <div class="form-field checklist-item-input">
                        <input
                          type="text"
                          [value]="item.label"
                          [placeholder]="'admin.editor.landingPage.itemLabel.placeholder' | localize"
                          (input)="updateChecklistItemLabel(i, $any($event.target).value)"
                        >
                      </div>
                      <button type="button" class="btn-remove" (click)="removeChecklistItem(i)">
                        {{ 'admin.editor.landingPage.removeItem' | localize }}
                      </button>
                    </div>
                  }
                </div>
              }

              @if (landingPageChecklistError) {
                <p class="error-text">{{ 'admin.editor.landingPage.error.checklistRequired' | localize }}</p>
              }

              <button type="button" class="btn-add" (click)="addChecklistItem()">
                {{ 'admin.editor.landingPage.addItem' | localize }}
              </button>
            </div>
          </div>
        }
      </section>

      <section class="form-section">
        <header class="section-header">
          <h2>{{ 'admin.editor.section.fields' | localize }}</h2>
          <button type="button" class="btn-add" (click)="addField()">{{ 'admin.editor.addField' | localize }}</button>
        </header>

        @if (fields.length === 0) {
          <div class="empty-fields">
            <p>{{ 'admin.editor.empty.fields' | localize }}</p>
          </div>
        } @else {
          <div class="fields-list" cdkDropList (cdkDropListDropped)="onFieldDrop($event)">
            @for (field of fields; track $index; let i = $index) {
              <div class="field-card" cdkDrag>
                <div cdkDragPlaceholder class="drag-placeholder"></div>
                <div class="field-card-header">
                  <span class="field-position">{{ i + 1 }}</span>
                  <span class="drag-handle" cdkDragHandle>
                    <i class="fa-solid fa-grip-vertical"></i>
                  </span>
                  <div class="reorder-buttons">
                    <button #moveUpBtn type="button" class="btn-reorder" (click)="moveFieldUp(i)" [disabled]="isFirstField(i)" aria-label="Move field up">
                      <i class="fa-solid fa-arrow-up"></i>
                    </button>
                    <button #moveDownBtn type="button" class="btn-reorder" (click)="moveFieldDown(i)" [disabled]="isLastField(i)" aria-label="Move field down">
                      <i class="fa-solid fa-arrow-down"></i>
                    </button>
                  </div>
                </div>
                <div class="field-row">
                  <div class="form-field">
                    <label>{{ 'admin.editor.field.id' | localize }} {{ 'common.required' | localize }}</label>
                    <input
                      type="text"
                      [value]="field.id"
                      (input)="updateField(i, 'id', $any($event.target).value)"
                      [placeholder]="'admin.editor.field.id.placeholder' | localize"
                    >
                  </div>
                  <div class="form-field">
                    <label>{{ 'admin.editor.field.label' | localize }} {{ 'common.required' | localize }}</label>
                    <input
                      type="text"
                      [value]="field.label"
                      (input)="updateField(i, 'label', $any($event.target).value)"
                      [placeholder]="'admin.editor.field.label.placeholder' | localize"
                    >
                  </div>
                  <div class="form-field">
                    <label>{{ 'admin.editor.field.type' | localize }}</label>
                    <select
                      [value]="field.type"
                      (change)="updateField(i, 'type', $any($event.target).value)"
                    >
                      @for (type of fieldTypes; track type.value) {
                        <option [value]="type.value">{{ type.labelKey | localize }}</option>
                      }
                    </select>
                  </div>
                  <div class="form-field checkbox-field">
                    <label>
                      <input
                        type="checkbox"
                        [checked]="field.required"
                        (change)="updateField(i, 'required', $any($event.target).checked)"
                      >
                      {{ 'admin.editor.field.required' | localize }}
                    </label>
                  </div>
                  <button type="button" class="btn-remove" (click)="removeField(i)">{{ 'admin.editor.removeField' | localize }}</button>
                </div>
                @if (field.type === 'autocomplete') {
                  <div class="autocomplete-config">
                    <div class="form-field">
                      <label>{{ 'admin.editor.autocomplete.sourceId' | localize }}</label>

                      @if (field.sourceId && uploadSuccess[field.sourceId]) {
                        <div class="upload-success">
                          <span class="tag is-success">
                            {{ 'admin.editor.autocomplete.uploaded' | localize }} ({{ uploadSuccess[field.sourceId] }} {{ 'admin.editor.autocomplete.entries' | localize }})
                          </span>
                        </div>
                      } @else if (field.sourceId) {
                        <div class="existing-source">
                          <span class="tag is-info">{{ 'admin.editor.autocomplete.sourceId' | localize }}: {{ field.sourceId }}</span>
                        </div>
                      }

                      @if (getExistingSourceIds().length > 0) {
                        <div class="existing-sources">
                          <label>{{ 'admin.editor.autocomplete.selectExisting' | localize }}</label>
                          <select (change)="assignExistingSourceId(i, $any($event.target).value)">
                            <option value="">{{ 'admin.editor.autocomplete.newSource' | localize }}</option>
                            @for (sid of getExistingSourceIds(); track sid) {
                              <option [value]="sid" [selected]="field.sourceId === sid">{{ sid }}</option>
                            }
                          </select>
                        </div>
                      }

                      <div class="upload-section">
                        <label>{{ 'admin.editor.autocomplete.uploadLabel' | localize }}</label>
                        <input type="file" accept=".json" (change)="uploadDataSource(i, $event)"
                               [disabled]="uploadingSourceId !== null">
                        @if (uploadingSourceId === (field.sourceId || field.id)) {
                          <span class="loader is-loading"></span>
                        }
                      </div>

                      @if (uploadError && uploadingSourceId === null) {
                        <p class="error-text">{{ uploadError }}</p>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }
      </section>

      <div class="form-actions">
        <button type="submit" class="btn-submit" [disabled]="submitting || form.invalid">
          @if (submitting) {
            {{ isEditMode ? ('admin.editor.submit.saving' | localize) : ('admin.editor.submit.creating' | localize) }}
          } @else {
            {{ isEditMode ? ('admin.editor.submit.save' | localize) : ('admin.editor.submit.create' | localize) }}
          }
        </button>
        <a routerLink="/admin/forms" class="btn-cancel">{{ 'admin.editor.cancel' | localize }}</a>
      </div>

      @if (isEditMode) {
        <div class="transition-actions">
          @if (formStatus() === 'draft') {
            <div class="buttons">
              <button
                type="button"
                class="button is-success is-outlined"
                (click)="dryRun()"
                [disabled]="dryRunLoading()">
                @if (dryRunLoading()) {
                  <span class="loader is-loading"></span>
                }
                {{ 'admin.forms.table.dryrun' | localize }}
              </button>
              <button
                type="button"
                class="button is-danger is-outlined"
                (click)="openDeleteDialog()"
                [disabled]="deleteLoading()">
                @if (deleteLoading()) {
                  <span class="loader is-loading"></span>
                }
                {{ 'admin.forms.table.delete' | localize }}
              </button>
            </div>
          }
          @if (formStatus() === 'dryrun') {
            <div class="buttons">
              <button
                type="button"
                class="button is-primary"
                (click)="openPublishDialog()"
                [disabled]="publishLoading()">
                @if (publishLoading()) {
                  <span class="loader is-loading"></span>
                }
                {{ 'admin.forms.table.publish' | localize }}
              </button>
              <button
                type="button"
                class="button is-danger is-outlined"
                (click)="openDeleteDialog()"
                [disabled]="deleteLoading()">
                @if (deleteLoading()) {
                  <span class="loader is-loading"></span>
                }
                {{ 'admin.forms.table.delete' | localize }}
              </button>
            </div>
          }
          @if (formStatus() === 'published') {
            <div class="buttons">
              <button
                type="button"
                class="button is-warning is-outlined"
                (click)="openUnpublishDialog()"
                [disabled]="unpublishLoading()">
                @if (unpublishLoading()) {
                  <span class="loader is-loading"></span>
                }
                {{ 'admin.forms.table.unpublish' | localize }}
              </button>
              <button
                type="button"
                class="button is-danger is-outlined"
                (click)="openDeleteDialog()"
                [disabled]="deleteLoading()">
                @if (deleteLoading()) {
                  <span class="loader is-loading"></span>
                }
                {{ 'admin.forms.table.delete' | localize }}
              </button>
            </div>
          }
        </div>
      }
    </form>
  }
</div>

@if (showPublishDialog()) {
  <app-confirmation-dialog
    [title]="'admin.forms.publish.dialog.title' | localize"
    [message]="'admin.forms.publish.dialog.message' | localize"
    [details]="(form.getRawValue().title || '') + ' (v' + (form.getRawValue().version || '') + ')'"
    [confirmText]="'admin.forms.publish.dialog.confirm' | localize"
    [cancelText]="'admin.forms.publish.dialog.cancel' | localize"
    [loading]="publishLoading()"
    (onConfirm)="confirmPublish()"
    (onCancel)="closePublishDialog()"
  />
}

@if (showDeleteDialog()) {
  <app-confirmation-dialog
    [title]="'admin.forms.delete.dialog.title' | localize"
    [message]="'admin.forms.delete.dialog.message' | localize"
    [details]="(form.getRawValue().title || '') + ' (v' + (form.getRawValue().version || '') + ')'"
    [confirmText]="'admin.forms.delete.dialog.confirm' | localize"
    [cancelText]="'admin.forms.delete.dialog.cancel' | localize"
    [loading]="deleteLoading()"
    (onConfirm)="confirmDelete()"
    (onCancel)="closeDeleteDialog()"
  />
}

@if (showUnpublishDialog()) {
  <app-confirmation-dialog
    [title]="'admin.forms.unpublish.dialog.title' | localize"
    [message]="'admin.forms.unpublish.dialog.message' | localize"
    [details]="(form.getRawValue().title || '') + ' (v' + (form.getRawValue().version || '') + ')'"
    [confirmText]="'admin.forms.unpublish.dialog.confirm' | localize"
    [cancelText]="'admin.forms.unpublish.dialog.cancel' | localize"
    [loading]="unpublishLoading()"
    (onConfirm)="confirmUnpublish()"
    (onCancel)="closeUnpublishDialog()"
  />
}
